/**
 * Serialize annotation persistence for one ticket across component unmounts
 * and remounts. The operation includes both JSON and raster publication, so a
 * older revisions stay ordered while they are locally active. A timeout
 * deliberately releases the queue so a non-cooperative operation cannot
 * wedge future edits; every server mutation used here must therefore carry
 * its optimistic annotation version so a request already received by the
 * server cannot commit stale state after its client-side abort.
 */
import {
  isAuthSessionCurrent,
  registerSessionAbortController,
  type AuthSessionEpoch,
} from '../../lib/authSessionEpoch';

const ticketTails = new Map<string, Promise<void>>();

export interface AnnotationPersistenceOptions<T> {
  ticketId: string;
  owner: AuthSessionEpoch;
  operation: (signal: AbortSignal) => Promise<T>;
  timeoutMs?: number;
  onTimeout?: () => void;
}

export type AnnotationPersistenceResult<T> =
  | { status: 'ok'; value: T }
  | { status: 'skipped' }
  | { status: 'timeout' };

export function enqueueAnnotationPersistence<T>(
  options: AnnotationPersistenceOptions<T>,
): Promise<AnnotationPersistenceResult<T>> {
  const { ticketId, owner, operation, timeoutMs = 15_000, onTimeout } = options;
  const queueKey = `${owner.epoch}:${owner.userId}:${ticketId}`;
  const previous = ticketTails.get(queueKey) ?? Promise.resolve();
  const run = async (): Promise<AnnotationPersistenceResult<T>> => {
    if (!isAuthSessionCurrent(owner)) return { status: 'skipped' };
    const controller = new AbortController();
    const unregister = registerSessionAbortController(owner, controller);
    let timedOut = false;
    const timeout = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, timeoutMs);
    const aborted = new Promise<never>((_resolve, reject) => {
      controller.signal.addEventListener(
        'abort',
        () => {
          reject(controller.signal.reason ?? new DOMException('Aborted', 'AbortError'));
        },
        { once: true },
      );
    });
    try {
      const value = await Promise.race([operation(controller.signal), aborted]);
      return { status: 'ok', value };
    } catch (error) {
      if (timedOut) {
        onTimeout?.();
        return { status: 'timeout' };
      }
      if (!isAuthSessionCurrent(owner) && controller.signal.aborted) return { status: 'skipped' };
      throw error;
    } finally {
      clearTimeout(timeout);
      unregister();
    }
  };
  const result = previous.then(run, run);
  const tail = result.then(
    () => undefined,
    () => undefined,
  );
  ticketTails.set(queueKey, tail);
  void tail.then(() => {
    if (ticketTails.get(queueKey) === tail) ticketTails.delete(queueKey);
  });
  return result;
}
